/**
 * Paletas por papel.
 *
 * Las de `PALETTES` guardan ocho colores por posición, y al aplicarlas se
 * reinterpretaban: la cuarta —el borde que la paleta había elegido— se tiraba y
 * se derivaba otro, y el botón no era el primario de la paleta sino una mezcla
 * calculada entre el primario y el acento, un color que nadie había elegido.
 * Por eso un H1, un botón o una tarjeta salían de otro tono del que se veía en
 * la muestra.
 *
 * Aquí cada color dice para qué es, y cada uno va a un solo sitio del tema:
 *
 * | papel        | variable              | pinta                                     |
 * |--------------|-----------------------|-------------------------------------------|
 * | `background` | `--wb-background`     | el fondo de la página y de la navegación  |
 * | `alternate`  | `--wb-backgroundSecondary` | las secciones que alternan, el fondo de los iconos |
 * | `surface`    | `--wb-surface`        | rellenos suaves: etiquetas, campos, placas |
 * | `card`       | `--wb-card`           | las tarjetas —nunca igual que el fondo—    |
 * | `border`     | `--wb-border`         | bordes y separadores                       |
 * | `ink`        | `--wb-text`           | los títulos; el párrafo es esta tinta suavizada hacia el fondo |
 * | `button`     | `--wb-primary`        | el color de marca: relleno del botón principal y contorno del secundario |
 * | `accent`     | `--wb-accent`         | enlaces, antetítulos, iconos y la tarjeta de color |
 * | (derivado)   | `--wb-muted`          | el texto secundario de los bloques: fechas, etiquetas, notas |
 *
 * `--wb-muted` no es un color de la paleta: es el gris entre la tinta y el
 * fondo. Los bloques lo usan para el texto apagado, y cuando además pintaba
 * los botones, un botón amarillo volvía amarillas las fechas y los títulos.
 *
 * El texto encima del botón no es un color de la paleta: se elige al aplicar,
 * el que se lea mejor sobre el relleno (ver `applyColorsToTheme`).
 */
import { relativeLuminance, resolvedSurfaceColor } from './surfaceColor'

export type ColorPalette = {
  id: string
  tags: string[]
  background: string
  alternate: string
  surface: string
  card: string
  border: string
  ink: string
  button: string
  accent: string
  /**
   * El texto secundario, cuando alguien lo fijó con su código (ver
   * `CustomPalette`). Sin él, sale de mezclar la tinta con el fondo.
   */
  muted?: string
}

/** Contraste WCAG entre dos colores sólidos; 1 si alguno no se entiende. */
export function contrastRatio(a: string, b: string): number {
  const ca = resolvedSurfaceColor(a)
  const cb = resolvedSurfaceColor(b)
  if (!ca || !cb) return 1
  const la = relativeLuminance(ca)
  const lb = relativeLuminance(cb)
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05)
}

/** De los candidatos, el que mejor se lee sobre `surface`. */
export function mostReadableOn(surface: string, candidates: readonly string[]): string {
  return candidates.reduce((best, candidate) => (contrastRatio(surface, candidate) > contrastRatio(surface, best) ? candidate : best))
}

/** Mezcla dos hex en sRGB: `t` es cuánto del segundo. */
function mixHex(a: string, b: string, t: number) {
  const ca = resolvedSurfaceColor(a)
  const cb = resolvedSurfaceColor(b)
  if (!ca || !cb) return a
  return `#${ca.map((value, index) => Math.round(value + (cb[index]! - value) * t).toString(16).padStart(2, '0')).join('')}`.toUpperCase()
}

/**
 * Por debajo de este contraste, una tarjeta apenas se distingue de lo que tiene
 * detrás. Un blanco sobre un gris claro de los de siempre (#F4F6F8) lo pasa;
 * uno sobre un gris casi blanco (#F7F8F9), no.
 */
export const CARD_SEPARATION_CONTRAST = 1.07

/**
 * El fondo de tarjeta para una sección de fondo `ground`, o `null` si la de la
 * paleta ya se distingue de él.
 *
 * Las secciones alternan entre el fondo de la página y el alterno, y la
 * tarjeta tenía un solo color: en muchas paletas es IGUAL a uno de los dos
 * (blanca sobre blanco, o el mismo gris que el alterno), así que en la mitad de
 * las secciones desaparecía. Aquí se elige, para cada fondo, el tono que se
 * separa de él: primero el otro fondo de la paleta —sigue siendo su color—, y
 * si tampoco, un paso más oscuro del propio fondo (o más claro en uno oscuro).
 */
export function cardSurfaceOn(ground: string, colors: { card?: string; background?: string; backgroundSecondary?: string; text?: string }): string | null {
  const card = colors.card
  if (!card || !resolvedSurfaceColor(ground) || !resolvedSurfaceColor(card)) return null
  if (contrastRatio(card, ground) >= CARD_SEPARATION_CONTRAST) return null
  const groundRgb = resolvedSurfaceColor(ground)!
  const dark = relativeLuminance(groundRgb) < 0.2
  /* El otro fondo sólo si es del mismo lado —claro sobre claro, oscuro sobre
     oscuro—. Con un alterno oscuro (#1A1625) y una sección clara, la tarjeta
     salía negra con el texto de la sección, también oscuro: ilegible (lo vio
     él en un sitio generado, 2026-10-22). */
  for (const candidate of [colors.background, colors.backgroundSecondary]) {
    const rgb = candidate ? resolvedSurfaceColor(candidate) : null
    if (candidate && rgb && (relativeLuminance(rgb) < 0.2) === dark && contrastRatio(candidate, ground) >= CARD_SEPARATION_CONTRAST) return candidate
  }
  /* Un paso del propio fondo hacia la tinta de la paleta —el teal de una
     clínica, el marrón de un café—, no hacia el negro: sigue siendo su color.
     En un fondo oscuro, hacia el blanco. Lo justo para que se distinga. */
  const toward = dark ? '#FFFFFF' : colors.text && resolvedSurfaceColor(colors.text) ? colors.text : '#000000'
  let step = mixHex(ground, toward, 0.04)
  for (let amount = 0.04; amount <= 0.3 && contrastRatio(step, ground) < CARD_SEPARATION_CONTRAST; amount += 0.02) {
    step = mixHex(ground, toward, amount)
  }
  return step
}

/** Si dos colores sólidos representan exactamente el mismo RGB. */
function sameColor(a: string, b: string): boolean {
  const ca = resolvedSurfaceColor(a)
  const cb = resolvedSurfaceColor(b)
  if (ca && cb) return ca.every((channel, index) => channel === cb[index])
  return a.trim().toLowerCase() === b.trim().toLowerCase()
}

/**
 * El primario, separado del acento sin abandonar su familia.
 *
 * Botones y tarjetas de acento aparecen juntos con frecuencia. Cuando los dos
 * papeles llevan el mismo color dejan de leerse como piezas distintas, así que
 * el acento conserva el color elegido y el primario se aclara apenas. El
 * segundo camino sólo cubre blanco puro, que no puede aclararse más.
 */
export function distinctPrimaryColor(primary: string, accent: string): string {
  if (!sameColor(primary, accent)) return primary
  const lighter = mixHex(primary, '#FFFFFF', 0.14)
  if (!sameColor(lighter, accent)) return lighter
  return mixHex(primary, '#000000', 0.14)
}

/** El texto secundario: la tinta llevada hacia el fondo, legible pero apagada. */
export function mutedTextColor(palette: Pick<ColorPalette, 'ink' | 'background' | 'muted'>) {
  /* El que dio el cliente, si se lee sobre el fondo (3:1, lo de un texto de
     apoyo); si no, el de siempre. */
  if (palette.muted && contrastRatio(palette.muted, palette.background) >= 3) return palette.muted
  return mixHex(palette.ink, palette.background, 0.42)
}

/**
 * Los nueve valores en el orden de `COLOR_KEYS` —primario, secundario,
 * superficie, fondo, muted, texto, acento, tarjeta y borde—, listos para
 * `applyColorsToTheme`.
 */
export function paletteColorValues(palette: ColorPalette): string[] {
  return [distinctPrimaryColor(palette.button, palette.accent), palette.alternate, palette.surface, palette.background, mutedTextColor(palette), palette.ink, palette.accent, distinctCardColor(palette), palette.border]
}

/** Lo mínimo para que dos superficies se distingan sin depender de un borde. */
export const MIN_SURFACE_CONTRAST = 1.1

/**
 * El escalón de una tarjeta clara sobre un fondo claro: se ve, pero no pide
 * gris. Las tarjetas llevan además su borde; el relleno sólo tiene que no ser
 * el del fondo.
 */
export const MIN_LIGHT_CARD_CONTRAST = 1.03

/** El contraste que una tarjeta necesita con el fondo, según sea claro u oscuro. */
export function cardContrastFloor(background: string): number {
  const color = resolvedSurfaceColor(background)
  return color && relativeLuminance(color) >= 0.5 ? MIN_LIGHT_CARD_CONTRAST : MIN_SURFACE_CONTRAST
}

/**
 * La tarjeta, distinta del fondo Y del fondo alterno.
 *
 * Una tarjeta del color de la sección en la que cae no se ve: en muchas
 * paletas la tarjeta era igual al alterno —o al fondo— y, en una sección de ese
 * color, las tarjetas desaparecían y sólo quedaba el texto flotando.
 *
 * En un tema claro la tarjeta es un plano MÁS CLARO, nunca un gris: antes se
 * pedía un contraste de 1.1 con los dos fondos a la vez y, sobre blanco, la
 * única salida era oscurecerla hacia la tinta — casi todas las paletas claras
 * acababan en un #E3E3E3 sin el matiz de la paleta. Ahora, por orden:
 *
 *  1. La de la paleta, si ya se distingue.
 *  2. Aclarada hacia blanco: blanco sobre crema, sobre gris perla…
 *  3. Sin sitio por arriba (fondo blanco): un tono ENTRE el fondo y el
 *     alterno, que conserva el matiz de la paleta.
 *  4. Si el alterno está pegado al fondo, un poco más allá del alterno, en su
 *     misma dirección: más tinte, no más gris.
 *
 * En un tema oscuro se hace lo de siempre: se aclara, que es como se ve una
 * superficie elevada sobre negro.
 */
export function distinctCardColor(palette: Pick<ColorPalette, 'card' | 'background' | 'alternate' | 'ink'>): string {
  const floor = cardContrastFloor(palette.background)
  const distinct = (color: string) =>
    contrastRatio(color, palette.background) >= floor && contrastRatio(color, palette.alternate) >= floor
  if (distinct(palette.card)) return palette.card

  if (floor === MIN_LIGHT_CARD_CONTRAST) {
    for (let step = 1; step <= 40; step += 1) {
      const candidate = mixHex(palette.card, '#FFFFFF', step * 0.025)
      if (distinct(candidate)) return candidate
    }
    for (let step = 1; step <= 19; step += 1) {
      const candidate = mixHex(palette.background, palette.alternate, step * 0.05)
      if (distinct(candidate)) return candidate
    }
    for (let step = 1; step <= 40; step += 1) {
      const candidate = extendHex(palette.background, palette.alternate, 1 + step * 0.05)
      if (distinct(candidate)) return candidate
    }
    return palette.card
  }

  /* Oscuro: blanco primero, una card se percibe como el plano que está más
     cerca; luego la tinta del tema, que conserva mejor su matiz que el negro. */
  for (const target of ['#FFFFFF', palette.ink, '#000000']) {
    for (let step = 1; step <= 20; step += 1) {
      const candidate = mixHex(palette.card, target, step * 0.025)
      if (distinct(candidate)) return candidate
    }
  }
  return palette.card
}

/** Como `mixHex`, pero pasado el segundo color (`t` > 1), en la misma dirección. */
function extendHex(a: string, b: string, t: number) {
  const ca = resolvedSurfaceColor(a)
  const cb = resolvedSurfaceColor(b)
  if (!ca || !cb) return a
  return `#${ca.map((value, index) => Math.max(0, Math.min(255, Math.round(value + (cb[index]! - value) * t))).toString(16).padStart(2, '0')).join('')}`.toUpperCase()
}

const p = (id: string, tags: string[], [background, alternate, surface, card, border, ink, button, accent]: string[]): ColorPalette => ({
  id, tags, background: background!, alternate: alternate!, surface: surface!, card: card!, border: border!, ink: ink!,
  button: distinctPrimaryColor(button!, accent!), accent: accent!,
})

/**
 * Cuarenta y siete, cada una pensada entera: fondo casi blanco o casi negro, una
 * tarjeta que se distingue del fondo, un borde que se ve sin gritar, tinta
 * casi negra y un solo color de marca. Orden de cada lista: fondo, alterno,
 * superficie, tarjeta, borde, tinta, botón, acento.
 */
export const COLOR_PALETTES: ColorPalette[] = [
  // Claras y de producto
  p('pearl-blue', ['moderno'], ['#FFFFFF', '#F5F5F7', '#EDEDF0', '#F5F5F7', '#D9D9DE', '#1D1D1F', '#0071E3', '#0071E3']),
  p('graphite', ['moderno'], ['#FFFFFF', '#F4F4F5', '#E9E9EB', '#FFFFFF', '#E4E4E7', '#09090B', '#18181B', '#2563EB']),
  p('paper-ink', ['moderno'], ['#FFFFFF', '#F7F6F3', '#EFEEEA', '#FFFFFF', '#E9E9E7', '#191919', '#191919', '#2383E2']),
  p('studio-mono', ['moderno'], ['#FFFFFF', '#F5F5F5', '#EBEBEB', '#F5F5F5', '#E6E6E6', '#000000', '#000000', '#0D99FF']),
  p('trust-blue', ['moderno'], ['#FFFFFF', '#F7F7F8', '#EDEDF0', '#FFFFFF', '#E5E7EB', '#111827', '#2F6BFF', '#2F6BFF']),
  p('payments', ['moderno'], ['#FFFFFF', '#F6F9FC', '#E3E8EE', '#FFFFFF', '#E3E8EE', '#0A2540', '#635BFF', '#635BFF']),
  p('commerce', ['moderno'], ['#FFFFFF', '#F3F4F6', '#E6E8EB', '#FFFFFF', '#D5D9D9', '#0F1111', '#FFD814', '#007185']),
  p('swiss-red', ['moderno'], ['#FFFFFF', '#F2F2F2', '#E5E5E5', '#F2F2F2', '#D9D9D9', '#000000', '#E30613', '#E30613']),
  p('cobalt', ['moderno'], ['#F8FAFC', '#EEF2F7', '#E2E8F0', '#FFFFFF', '#DCE3EC', '#0F172A', '#1D4ED8', '#1D4ED8']),
  p('pine', ['moderno'], ['#F7FAF9', '#ECF3F0', '#DCE8E3', '#FFFFFF', '#D5E3DD', '#0E2620', '#16856B', '#16856B']),
  /* La del estilo Coral (sitio de Lumen Dental Studio): menta clara, azul
     petróleo, botón coral y acento coral profundo. */
  p('coral-teal', ['moderno', 'calido'], ['#FFFFFF', '#F5F8F8', '#EEF3F3', '#F1F5F5', '#D1E7E4', '#0F5257', '#F27059', '#E3604E']),
  p('brutal-bone', ['moderno'], ['#F4F4F0', '#FFFFFF', '#E7E7E0', '#FFFFFF', '#000000', '#000000', '#FF5B37', '#D9431F']),
  p('nordic', ['moderno'], ['#F7F7F5', '#EEEEEA', '#E2E2DC', '#FFFFFF', '#DEDED8', '#1E2A2F', '#1E2A2F', '#4E6E7D']),

  // Suaves: pastel sobre crema, para lo tierno sin parecer de juguete. El
  // fondo y las superficies llevan el tono; el botón, el mismo color más
  // hondo, para que su texto se lea. Faltaban: un estudio de yoga «rosa
  // cuarzo» acababa en arena con botón negro.
  p('rose-quartz', ['suave'], ['#FFFAF9', '#FBEFEE', '#F5E1E0', '#FFFFFF', '#EED8D6', '#3A2528', '#B8607A', '#D9A3A8']),
  p('blush', ['suave', 'calido'], ['#FDF8F6', '#F8ECE8', '#F1DDD6', '#FFFFFF', '#EAD5CD', '#3B2A26', '#A8656F', '#C9928A']),
  p('peony-sand', ['suave', 'calido'], ['#FCF9F4', '#F6EEE4', '#EEDFD0', '#FFFFFF', '#E6D7C6', '#33281F', '#C0727F', '#B08A64']),
  p('dusty-rose', ['suave', 'elegante'], ['#FBF7F7', '#F3E9EA', '#E8D6D8', '#FFFFFF', '#E2CFD2', '#2A1A1E', '#8E4A5C', '#C48A97']),
  p('lavender', ['suave'], ['#FAF9FD', '#F1EEF9', '#E5E0F3', '#FFFFFF', '#DEDAEE', '#25213A', '#7A68B8', '#B7A6E0']),
  p('lilac-mist', ['suave', 'elegante'], ['#FCFAFC', '#F5EEF5', '#EADDEA', '#FFFFFF', '#E3D6E3', '#2E2230', '#94608F', '#C9A2C4']),
  p('peach', ['suave', 'calido'], ['#FFF9F5', '#FCEEE4', '#F7DECC', '#FFFFFF', '#F0D6C4', '#3A2418', '#C8673F', '#F2B391']),
  p('soft-coral', ['suave', 'calido'], ['#FFF8F6', '#FDEDE8', '#F9DBD2', '#FFFFFF', '#F2D3C9', '#3B1F19', '#D9614A', '#F5A58F']),
  p('butter', ['suave', 'calido'], ['#FFFDF6', '#FBF6E2', '#F5ECC8', '#FFFFFF', '#EDE3BF', '#2E2814', '#9C7A16', '#E8C766']),
  p('sage', ['suave'], ['#F8FAF6', '#EEF3EA', '#E0E9DA', '#FFFFFF', '#D9E3D2', '#1F2A1E', '#5E7A5A', '#A9BFA0']),
  p('mint', ['suave'], ['#F6FBF9', '#E9F5F0', '#D6ECE3', '#FFFFFF', '#D0E6DC', '#14281F', '#2F8A6B', '#8FD3B6']),
  p('sea-glass', ['suave'], ['#F6FBFA', '#E8F4F2', '#D3EAE6', '#FFFFFF', '#CDE4E0', '#12282A', '#3F8D86', '#A6D8D0']),
  p('powder-blue', ['suave'], ['#F8FBFD', '#ECF3F9', '#DCE9F3', '#FFFFFF', '#D4E3EF', '#16222E', '#4F7FA6', '#9EC3E0']),

  // Cálidas
  p('clay', ['calido'], ['#FAF9F5', '#F0EEE6', '#E8E6DC', '#FFFFFF', '#E3E0D6', '#141413', '#141413', '#C8643F']),
  p('stone', ['calido'], ['#F2F0EB', '#E9E6DF', '#DDD9D0', '#FAF9F6', '#D6D1C6', '#1C1C1A', '#1C1C1A', '#A8522F']),
  p('forest-calm', ['calido'], ['#FBFAF7', '#F1EFE8', '#E5E2D8', '#FFFFFF', '#E0DDD3', '#1F2A24', '#2F4A3D', '#3F6B52']),
  p('sand', ['calido'], ['#FDFCF8', '#F6F2E8', '#EAE3D2', '#FFFFFF', '#E2DACA', '#2A2418', '#2A2418', '#8C6A33']),
  p('terracotta', ['calido'], ['#FDFAF7', '#F7EEE7', '#EDDCD0', '#FFFFFF', '#E8D8CC', '#2E170F', '#B84F22', '#B84F22']),
  p('olive', ['calido'], ['#FBFAF5', '#F3F1E6', '#E6E3D0', '#FFFFFF', '#DEDAC6', '#2A2A14', '#4D5320', '#6B7328']),

  // Elegantes
  p('editorial', ['elegante'], ['#FFFFFF', '#F7F5F0', '#ECE8DF', '#F7F5F0', '#E4E0D6', '#111111', '#111111', '#B3261E']),
  p('ivory-gold', ['elegante'], ['#F6F4EF', '#EFEBE3', '#E4DED2', '#EFEBE3', '#D9D3C6', '#1B1B18', '#1B1B18', '#7A6644']),
  p('navy', ['elegante'], ['#FFFFFF', '#F4F6F8', '#E6EAEE', '#F4F6F8', '#D0D7DE', '#051C2C', '#051C2C', '#2251FF']),
  p('heritage', ['elegante'], ['#FFFFFF', '#F5F3EF', '#E8E4DC', '#FFFFFF', '#D8D2C6', '#1B2430', '#1B2430', '#7D6038']),
  p('burgundy', ['elegante'], ['#FBF9F9', '#F3ECED', '#E5D8DA', '#FFFFFF', '#E0D2D4', '#22101A', '#7F1D3A', '#7F1D3A']),
  p('plum', ['elegante'], ['#FBFAFB', '#F2EFF2', '#E3DCE4', '#FFFFFF', '#DDD5DE', '#1C1220', '#4C2A4F', '#6A4468']),
  p('deep-teal', ['elegante'], ['#F5FAFA', '#E7F2F2', '#CFE6E6', '#FFFFFF', '#CFE0E0', '#081514', '#134E4A', '#0E6F80']),
  p('slate', ['elegante'], ['#F9FAFB', '#EEF1F4', '#DDE3E9', '#FFFFFF', '#D6DDE4', '#16202B', '#16202B', '#4A6E96']),

  // Oscuras
  p('precision', ['nocturno'], ['#08090A', '#0F1012', '#17181B', '#0F1012', '#23252A', '#F7F8F8', '#F7F8F8', '#8B93F8']),
  p('pure-black', ['nocturno'], ['#000000', '#0A0A0A', '#111111', '#0A0A0A', '#262626', '#EDEDED', '#EDEDED', '#3291FF']),
  p('midnight-teal', ['nocturno'], ['#0B0C0E', '#141619', '#1E2126', '#141619', '#2A2E35', '#F5F6F7', '#4FD1C5', '#4FD1C5']),
  p('obsidian-gold', ['nocturno'], ['#0A0A0A', '#151515', '#1F1F1F', '#151515', '#2A2A2A', '#F2F2F2', '#C6A15B', '#C6A15B']),
  p('forest-night', ['nocturno'], ['#0B100E', '#141A17', '#1D2621', '#141A17', '#26302A', '#F1F4F2', '#5FA37E', '#7FC29B']),
  p('deco-night', ['nocturno'], ['#101418', '#171C22', '#1F262E', '#171C22', '#2B343E', '#F3EEE4', '#D4AF6A', '#D4AF6A']),
  p('rose-night', ['nocturno', 'suave'], ['#151012', '#1E171A', '#291F23', '#1E171A', '#3A2C31', '#F6ECEE', '#E8A0B4', '#E8A0B4']),
]

/** Los grupos en que se enseñan las paletas, en este orden. */
export const PALETTE_GROUP_IDS = ['moderno', 'suave', 'calido', 'elegante', 'nocturno'] as const

/**
 * Una muestra variada para una fila corta (Marca, en el panel): las primeras
 * de cada grupo, por turnos. Las doce primeras del catálogo eran todas
 * «modernas» —azules y grises— y nunca salía una rosa ni una oscura.
 */
export function paletteSampler(count: number): ColorPalette[] {
  const byGroup = PALETTE_GROUP_IDS.map((group) => COLOR_PALETTES.filter((palette) => palette.tags[0] === group))
  const picked: ColorPalette[] = []
  for (let round = 0; picked.length < count && byGroup.some((list) => list.length > round); round++) {
    for (const list of byGroup) if (list[round] && picked.length < count) picked.push(list[round]!)
  }
  return picked
}
